import { ripemd160 } from '@noble/hashes/ripemd160';
import { sha256 } from '@noble/hashes/sha256';
import { sha512_256 } from '@noble/hashes/sha512';
import { utils } from '@noble/secp256k1';
import { Buffer, bytesToHex, with0x } from '@stacks/common';
import { c32addressDecode } from 'c32check';
import lodashCloneDeep from 'lodash.clonedeep';
import { deserializeCV, serializeCV } from './clarity';
export const randomBytes = (bytesLength) => Buffer.from(utils.randomBytes(bytesLength));
export { bytesToHex };
export class BufferArray {
    constructor() {
        this._value = [];
    }
    get value() {
        return this._value;
    }
    appendHexString(hexString) {
        this.value.push(Buffer.from(hexString, 'hex'));
    }
    push(buffer) {
        return this._value.push(buffer);
    }
    appendByte(octet) {
        if (!Number.isInteger(octet) || octet < 0 || octet > 255) {
            throw new Error(`Value ${octet} is not a valid byte`);
        }
        this.value.push(Buffer.from([octet]));
    }
    concatBuffer() {
        return Buffer.concat(this.value);
    }
}
export const leftPadHex = (hexString) => hexString.length % 2 == 0 ? hexString : `0${hexString}`;
export const leftPadHexToLength = (hexString, length) => hexString.padStart(length, '0');
export const rightPadHexToLength = (hexString, length) => hexString.padEnd(length, '0');
export const exceedsMaxLengthBytes = (string, maxLengthBytes) => string ? Buffer.from(string).length > maxLengthBytes : false;
export function cloneDeep(obj) {
    return lodashCloneDeep(obj);
}
export function omit(obj, prop) {
    const clone = cloneDeep(obj);
    delete clone[prop];
    return clone;
}
export const txidFromData = (data) => {
    return Buffer.from(sha512_256(data)).toString('hex');
};
export const hash160 = (input) => {
    return Buffer.from(ripemd160(sha256(input)));
};
export const hashP2PKH = (input) => {
    return hash160(input).toString('hex');
};
export const hashP2WPKH = (input) => {
    const keyHash = hash160(input);
    const bufferArray = new BufferArray();
    bufferArray.appendByte(0);
    bufferArray.appendByte(keyHash.length);
    bufferArray.push(keyHash);
    const redeemScript = bufferArray.concatBuffer();
    const redeemScriptHash = hash160(redeemScript);
    return redeemScriptHash.toString('hex');
};
export const hashP2SH = (numSigs, pubKeys) => {
    if (numSigs > 15 || pubKeys.length > 15) {
        throw Error('P2SH multisig address can only contain up to 15 public keys');
    }
    const bufferArray = new BufferArray();
    bufferArray.appendByte(80 + numSigs);
    pubKeys.forEach(pubKey => {
        bufferArray.appendByte(pubKey.length);
        bufferArray.push(pubKey);
    });
    bufferArray.appendByte(80 + pubKeys.length);
    bufferArray.appendByte(174);
    const redeemScript = bufferArray.concatBuffer();
    const redeemScriptHash = hash160(redeemScript);
    return redeemScriptHash.toString('hex');
};
export const hashP2WSH = (numSigs, pubKeys) => {
    if (numSigs > 15 || pubKeys.length > 15) {
        throw Error('P2WSH multisig address can only contain up to 15 public keys');
    }
    const scriptArray = new BufferArray();
    scriptArray.appendByte(80 + numSigs);
    pubKeys.forEach(pubKey => {
        scriptArray.appendByte(pubKey.length);
        scriptArray.push(pubKey);
    });
    scriptArray.appendByte(80 + pubKeys.length);
    scriptArray.appendByte(174);
    const script = scriptArray.concatBuffer();
    const digest = Buffer.from(sha256(script));
    const bufferArray = new BufferArray();
    bufferArray.appendByte(0);
    bufferArray.appendByte(digest.length);
    bufferArray.push(digest);
    const redeemScript = bufferArray.concatBuffer();
    const redeemScriptHash = hash160(redeemScript);
    return redeemScriptHash.toString('hex');
};
export function isClarityName(name) {
    const regex = /^[a-zA-Z]([a-zA-Z0-9]|[-_!?+<>=/*])*$|^[-+=/*]$|^[<>]=?$/;
    return regex.test(name) && name.length < 128;
}
export function cvToHex(cv) {
    const serialized = serializeCV(cv);
    return `0x${serialized.toString('hex')}`;
}
export function hexToCV(hex) {
    return deserializeCV(hex);
}
export const parseReadOnlyResponse = (response) => {
    if (response.okay) {
        return hexToCV(response.result);
    }
    else {
        throw new Error(response.cause);
    }
};
export const validateStacksAddress = (stacksAddress) => {
    try {
        c32addressDecode(stacksAddress);
        return true;
    }
    catch (e) {
        return false;
    }
};
export const validateTxId = (txid) => {
    if (txid === 'success')
        return true;
    const value = with0x(txid).toLowerCase();
    if (value.length !== 66)
        return false;
    return with0x(BigInt(value).toString(16).padStart(64, '0')) === value;
};
//# sourceMappingURL=utils.js.map